import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient, recallApiKey, resolveApiBase, resolveSdkBase } from '../api.js';
import { KeyIcon, CopyIcon, CheckIcon } from '../components/icons';

/**
 * Production embed install page.
 *
 * Different audience from /bookmarklet — that page is the operator's
 * personal admin bookmark; this one is the snippet a customer pastes
 * into their site's <head>.  Same SDK, different key class:
 *
 *   - Bookmarklet uses a personal sk_live_* (operator's identity
 *     rides along on JWT, key bakes into the bookmark URL).
 *   - Embed uses a system-owned pk_live_* publishable key (safe in
 *     page source — origin allow-list plus the read-only public
 *     scope set keep it inert from a non-allowed origin).
 *
 * Auto-mint on project create (card 002) means the project already
 * has a pk_live_* embed key the moment you create the project, so
 * this page can show a ready-to-paste snippet without first sending
 * the user off to the API keys tab.  If somehow there isn't one (key
 * archived, ancient project predating auto-mint), we surface a clear
 * CTA pointing at the SDK embed preset on the API keys tab.
 */
export function Install() {
  const { getToken } = useAuth();
  const outlet = useOutletContext();
  const { slug: urlSlug } = useParams();
  const scopedToProject = Boolean(urlSlug);
  const projectId = outlet?.project?.project_id || null;

  const api = useMemo(
    () => createApiClient(getToken, { projectId }),
    [getToken, projectId],
  );

  const apiBase = resolveApiBase();
  const defaultSdkBase = resolveSdkBase()
    || (typeof window !== 'undefined' ? window.location.origin : '');

  const [apiKey, setApiKey] = useState('');
  const [sdkBase] = useState(defaultSdkBase);
  const [projects, setProjects] = useState([]);
  const [projectSlug, setProjectSlug] = useState(urlSlug || '');
  const [embedKeys, setEmbedKeys] = useState([]);
  const [keysLoaded, setKeysLoaded] = useState(false);

  // Tenant-wide variant: pick a project before the snippet appears,
  // since each project's embed snippet bakes its own pk_live_* key.
  useEffect(() => {
    if (scopedToProject) return;
    let alive = true;
    api('/v1/admin/projects').then((data) => {
      if (!alive) return;
      const list = Array.isArray(data) ? data : (data?.items || []);
      setProjects(list);
      if (list[0]?.slug && !projectSlug) setProjectSlug(list[0].slug);
    }).catch(() => { /* optional context */ });
    return () => { alive = false; };
  }, [api, scopedToProject, projectSlug]);

  // Inside a project, fetch its API keys and filter to publishable
  // embed keys (kind === 'pk', system-owned).  These are the keys
  // safe to put on the customer's production page.  Personal pk
  // keys (the Bookmarklet preset mints those) also work, but they
  // auto-revoke on offboarding which is dangerous for a prod embed —
  // prefer system-owned ones and only fall back if none exist.
  useEffect(() => {
    if (!scopedToProject || !projectId) {
      setEmbedKeys([]);
      setKeysLoaded(true);
      return;
    }
    let alive = true;
    setKeysLoaded(false);
    api(`/v1/admin/api-keys?project_id=${encodeURIComponent(projectId)}&owner_kind=system`)
      .then((resp) => {
        if (!alive) return;
        const all = resp?.keys || [];
        // Card 042 — only system-owned pk_live for this project.
        // Personal pk_live (bookmarklet-style) belong on the
        // Bookmarklet page; they auto-revoke on offboarding which
        // is the opposite of what a production embed wants.
        // Defence-in-depth: backend already filtered to
        // owner_kind=system but we re-check the kind + owner_user_id
        // emptiness here so a future regression in the server-side
        // filter can't surface someone else's key on this page.
        const publishable = all.filter((k) => (
          (k.kind || 'sk') === 'pk'
          && k.active !== false
          && !k.owner_user_id
          && !k.is_metadata_only
        ));
        setEmbedKeys(publishable);
        setKeysLoaded(true);
      })
      .catch(() => { if (alive) setKeysLoaded(true); });
    return () => { alive = false; };
  }, [api, scopedToProject, projectId]);

  // Default the snippet's key field to the best available embed key:
  // either the freshly-recalled plaintext we cached at mint time, or
  // the prefix (so the snippet at least shows the right shape and a
  // bright "paste the secret" affordance).
  useEffect(() => {
    if (!keysLoaded || apiKey) return;
    const best = embedKeys[0];
    if (!best) return;
    const cached = recallApiKey(best.prefix);
    setApiKey(cached || best.prefix);
  }, [keysLoaded, embedKeys, apiKey]);

  const apiKeysHref = scopedToProject
    ? `/projects/${urlSlug}/api-keys`
    : (projectSlug ? `/projects/${projectSlug}/api-keys` : '/projects');

  const bookmarkletHref = scopedToProject
    ? `/projects/${urlSlug}/bookmarklet`
    : '/bookmarklet';

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>Install</h1>
          <p className="page__subtitle">
            The production <code>&lt;script&gt;</code> snippet to paste into
            your site's <code>&lt;head&gt;</code>. Uses a publishable{' '}
            <code>pk_live_*</code> key — safe to ship in page source
            because origin allow-list plus the read-only public scope
            set keep it inert anywhere off-domain.
          </p>
        </div>
      </div>

      {!scopedToProject && (
        <section className="card">
          <h3 className="card__title">Step 1 — pick a project</h3>
          {projects.length === 0 ? (
            <p className="card__body">
              No projects yet — <Link to="/projects">create one</Link> first.
              Each project gets its own publishable embed key auto-minted
              at create time.
            </p>
          ) : (
            <label className="field" style={{ maxWidth: 320 }}>
              <span className="field__label">Project</span>
              <select
                className="select"
                value={projectSlug}
                onChange={(e) => setProjectSlug(e.target.value)}
              >
                {projects.map((p) => (
                  <option key={p.project_id} value={p.slug}>
                    {p.name} {p.is_default ? '· (default)' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
        </section>
      )}

      {scopedToProject && (
        <EmbedKeyPicker
          embedKeys={embedKeys}
          keysLoaded={keysLoaded}
          apiKey={apiKey}
          setApiKey={setApiKey}
          apiKeysHref={apiKeysHref}
        />
      )}

      <InstallSnippet
        apiKey={apiKey}
        sdkBase={sdkBase}
        apiBase={apiBase}
        projectSlug={projectSlug}
        project={outlet?.project}
        scopedToProject={scopedToProject}
      />

      <section className="card" style={{ background: 'var(--surface-1)' }}>
        <p className="card__body" style={{ margin: 0 }}>
          Looking for the bookmarklet you drop into your bookmark bar?{' '}
          <Link to={bookmarkletHref}>Spotlight bookmarklet</Link> — uses
          a personal <code>sk_live_*</code> key and never touches your
          production page.
        </p>
      </section>

      <style>{INSTALL_CSS}</style>
    </div>
  );
}

/**
 * Embed key picker.
 *
 * Default-selects the best system-owned pk_live_* on mount via the
 * parent's effect — this component just renders the chooser when more
 * than one exists, and the empty-state CTA when none do.
 */
function EmbedKeyPicker({ embedKeys, keysLoaded, apiKey, setApiKey, apiKeysHref }) {
  if (!keysLoaded) {
    return (
      <section className="card">
        <h3 className="card__title">Step 1 — embed key</h3>
        <p className="card__body" style={{ color: 'var(--text-3)' }}>
          Loading the project's publishable keys…
        </p>
      </section>
    );
  }

  if (embedKeys.length === 0) {
    return (
      <section className="card">
        <h3 className="card__title">Step 1 — embed key</h3>
        <p className="card__body">
          No publishable embed key on this project yet. Mint one with
          the <strong>Embed in your public page (SDK)</strong> preset on
          the <Link to={apiKeysHref}>API keys tab</Link> — that preset
          mints a system-owned <code>pk_live_*</code> locked to the
          read-only public SDK scopes, which is exactly what you want
          here.
        </p>
      </section>
    );
  }

  return (
    <section className="card">
      <h3 className="card__title">Step 1 — embed key</h3>
      <p className="card__body">
        We picked the best publishable key for this project automatically.
        If you minted multiple, swap below. The snippet below updates as
        you change the selection.
      </p>
      <ul className="list" style={{ marginTop: 10 }}>
        {embedKeys.map((k) => {
          const cached = recallApiKey(k.prefix);
          const value = cached || k.prefix;
          const selected = apiKey === value;
          return (
            <li key={k.prefix} className="list__row">
              <div className="list__primary">
                <span className="list__name">
                  {k.name || <em style={{ color: 'var(--text-3)' }}>untitled</em>}
                  {selected && (
                    <span className="badge badge--default" style={{ marginLeft: 8 }}>
                      selected
                    </span>
                  )}
                  <span
                    className="badge badge--default"
                    style={{ marginLeft: 6 }}
                    title="System-owned — survives anyone offboarding."
                  >
                    system
                  </span>
                </span>
                <span className="list__meta">
                  <code>{k.prefix}…</code>
                  {!cached && (
                    <> · <span style={{ color: 'var(--text-3)' }}>plaintext not cached here; you'll paste the snippet exactly once</span></>
                  )}
                </span>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button
                  type="button"
                  className="btn btn--secondary"
                  onClick={() => setApiKey(value)}
                  title={cached
                    ? 'Use this key in the snippet'
                    : 'Load the prefix; mint a new one if you no longer have the plaintext'}
                >
                  <KeyIcon /> Use this key
                </button>
              </div>
            </li>
          );
        })}
      </ul>
      <p className="list__meta" style={{ marginTop: 10 }}>
        Need a fresh one? <Link to={apiKeysHref}>Mint a new embed key</Link>.
      </p>
    </section>
  );
}

/**
 * Permanent-install snippet.
 *
 * Shows both flavours (auto-init via data-api-key, or manual init)
 * and offers a one-click copy so the user doesn't have to stitch
 * the URL + key together by hand.
 */
function InstallSnippet({ apiKey, sdkBase, apiBase, projectSlug, project, scopedToProject }) {
  const [copied, setCopied] = useState('');
  const [cdnLatest, setCdnLatest] = useState(null);

  useEffect(() => {
    fetch(`${resolveSdkBase()}/manifest.json`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => setCdnLatest(m?.latest || null))
      .catch(() => setCdnLatest(null));
  }, []);

  const sdkPrefix = (sdkBase || '').replace(/\/+$/, '');
  const apiOrigin = (apiBase || '').replace(/\/+$/, '');

  const autoSnippet = apiKey
    ? (
`<script src="${sdkPrefix}/latest/spotlight.min.js"
        data-api-key="${apiKey}"
        defer></script>`
    )
    : null;

  const manualSnippet = apiKey
    ? (
`<script src="${sdkPrefix}/latest/spotlight.min.js" defer></script>
<script>
  Spotlight.init({
    apiKey: ${JSON.stringify(apiKey)},
    baseUrl: ${JSON.stringify(apiOrigin)},${projectSlug ? `
    project: ${JSON.stringify(projectSlug)},` : ''}
    userId: window.CURRENT_USER_ID,  // replace with your own
  });
</script>`
    )
    : null;

  function copy(kind, body) {
    if (!body || typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(body).then(() => {
      setCopied(kind);
      setTimeout(() => setCopied(''), 1500);
    }).catch(() => { /* older browsers */ });
  }

  return (
    <section className="card">
      <h3 className="card__title">Step 2 — paste this into your site</h3>
      <p className="card__body">
        <code>/sdk/latest/</code> rolls forward on every build — swap
        it for <code>/sdk/v{'{semver}'}/</code> to pin if you want zero
        surprise. Either way the key is visible in your page source,
        so lock it down via <strong>Sites</strong> (origin allow-list)
        and keep this project scoped.
      </p>

      {!apiKey && (
        <div className="state" style={{ marginTop: 12 }}>
          <div className="state__title">Pick an embed key above and the snippet fills in.</div>
        </div>
      )}

      {scopedToProject && (
        <VersionDriftBanner project={project} cdnLatest={cdnLatest} />
      )}

      {apiKey && (
        <>
          <div className="install-snippet" data-ui-name="install-snippet">
            <div className="install-snippet__head">
              <strong>Auto-init (simplest — reads key from the script tag)</strong>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => copy('auto', autoSnippet)}
                aria-label={copied === 'auto' ? 'Copied auto-init snippet' : 'Copy auto-init snippet'}
                title={copied === 'auto' ? 'Copied' : 'Copy to clipboard'}
              >
                {copied === 'auto' ? <CheckIcon /> : <CopyIcon />}
                {copied === 'auto' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre><code>{autoSnippet}</code></pre>
          </div>

          <div className="install-snippet" style={{ marginTop: 10 }}>
            <div className="install-snippet__head">
              <strong>Manual init (wire <code>userId</code> + anything extra)</strong>
              <button
                type="button"
                className="btn btn--ghost"
                onClick={() => copy('manual', manualSnippet)}
                aria-label={copied === 'manual' ? 'Copied manual-init snippet' : 'Copy manual-init snippet'}
                title={copied === 'manual' ? 'Copied' : 'Copy to clipboard'}
              >
                {copied === 'manual' ? <CheckIcon /> : <CopyIcon />}
                {copied === 'manual' ? 'Copied' : 'Copy'}
              </button>
            </div>
            <pre><code>{manualSnippet}</code></pre>
          </div>
        </>
      )}
    </section>
  );
}

function VersionDriftBanner({ project, cdnLatest }) {
  const observed = project?.observed_sdk_version || '';
  const at = project?.observed_at || '';

  if (!observed && !cdnLatest) return null;

  if (!observed) {
    return (
      <div className="state" style={{ marginTop: 12, textAlign: 'left' }}>
        <div className="state__title">No client heartbeats seen yet</div>
        <div>
          Once the SDK loads on a real page for this project, its
          reported bundle version will appear here. CDN currently
          publishes <code>v{cdnLatest || '?'}</code> as
          <code> latest</code>.
        </div>
      </div>
    );
  }

  const same = cdnLatest && observed === cdnLatest;
  return (
    <div
      className={`state${same ? '' : ' state--error'}`}
      style={{ marginTop: 12, textAlign: 'left' }}
    >
      <div className="state__title">
        {same
          ? <>Clients are on the latest SDK (v{observed})</>
          : <>Clients are on <code>v{observed}</code> — CDN latest is <code>v{cdnLatest || '?'}</code></>}
      </div>
      <div>
        {same
          ? 'Nothing to do. Heartbeat received '
          : <>Update the <code>&lt;script src&gt;</code> on your page
            to point at <code>/sdk/v{cdnLatest}/spotlight.min.js</code>{' '}
            (or leave it on <code>/sdk/latest/</code> which auto-rolls
            within ~60s). Most recent heartbeat received </>}
        {at ? <TimeAgo iso={at} /> : 'recently'}.
      </div>
    </div>
  );
}

function TimeAgo({ iso }) {
  try {
    const d = new Date(iso);
    const seconds = Math.round((Date.now() - d.getTime()) / 1000);
    if (seconds < 60) return <>just now</>;
    if (seconds < 3600) return <>{Math.round(seconds / 60)}m ago</>;
    if (seconds < 86400) return <>{Math.round(seconds / 3600)}h ago</>;
    if (seconds < 86400 * 7) return <>{Math.round(seconds / 86400)}d ago</>;
    return <>{d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>;
  } catch { return <>{iso}</>; }
}

const INSTALL_CSS = `
  .install-snippet {
    border: 1px solid var(--border-1);
    border-radius: var(--r-md);
    overflow: hidden;
  }
  .install-snippet__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: var(--surface-1);
    border-bottom: 1px solid var(--border-1);
  }
  .install-snippet pre {
    margin: 0;
    padding: 12px 14px;
    background: var(--text-1);
    color: var(--surface-muted);
    overflow-x: auto;
    font-size: 12px;
    line-height: 1.55;
  }
  .install-snippet pre code {
    background: transparent;
    padding: 0;
    color: inherit;
  }
`;
